import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { OrgMemberRole, OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { OrgCardAdapter } from '@makyoapp/makyo-common'
import { ArrowDown16, Edit16, Refresh16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { OrgCard as BaseOrgCard } from '@makyoapp/makyo-ui'

import { useCacheContext } from '@/context/CacheContext'
import { useOrgsContext } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'
import { ORG_APP_ROUTE, ORG_ROUTE } from '@/shared/constants'

interface OrgCardProps {
  org: OrgCardAdapter
  onDeleteClick?: (org: OrgCardAdapter) => void
  onEditClick?: (org: OrgCardAdapter) => void
  onTransferClick?: (org: OrgCardAdapter) => void
  onLeaveClick?: (org: OrgCardAdapter) => void
}

/**
 * extended OrgCard
 */
export function OrgCard({ org, onDeleteClick, onEditClick, onTransferClick, onLeaveClick }: OrgCardProps) {
  const { t } = useTranslation()
  const { userData } = useAuth()
  const { orgsPerms } = useOrgsContext()

  const { imageCache } = useCacheContext()
  const withCache = !!imageCache.values[`org-${org.id}`]

  const myRole = org.members.find((m) => m.userId === userData?.id)?.orgRole || OrgMemberRole.Member

  const isOwner = userData && org.ownerId === userData.id
  const filteredMembers = org.members.filter((om) => om.status === 'active')
  const isDefault = !!isOwner && org.isDefault

  const canLeave = !isOwner && onLeaveClick
  const canTransfer = !org.isDefault && onTransferClick && orgsPerms[org.id]?.[OrgPermission.Transfer]
  const canEdit = onEditClick && orgsPerms[org.id]?.[OrgPermission.ProfileEdit]
  const canDelete = !org.isDefault && orgsPerms[org.id]?.[OrgPermission.Remove] && onDeleteClick

  return (
    <BaseOrgCard
      id={org.id}
      name={org.name}
      memberCount={filteredMembers.length}
      memberCountTitle={t('Members')}
      teamCount={org.teamCount}
      teamCountTitle={t('Teams')}
      dbCount={org.dbCount}
      dbCountTitle={t('Databases')}
      appCount={org.appCount}
      appCountTitle={t('Apps')}
      avatar={org.avatar}
      avatarCache={withCache}
      myRole={myRole}
      isDefault={isDefault}
      defaultTooltip={t("This is your default organization, it can't be deleted")}
      renderLink={(content) => (
        <Link className='link' to={`${ORG_ROUTE}/${org.id}/${ORG_APP_ROUTE}`}>
          {content}
        </Link>
      )}
      menuBtnClassName='mk-org-menu-btn'
      menu={
        (canEdit || onDeleteClick || onTransferClick) && (
          <>
            {canEdit && (
              <BaseOrgCard.MenuItem
                className='mdd-edit-org-act'
                id={`${org.id}-edit-org-act`}
                onClick={() => onEditClick(org)}
              >
                <Edit16 />
                <div>{t('Edit')}</div>
              </BaseOrgCard.MenuItem>
            )}
            {canTransfer && (
              <BaseOrgCard.MenuItem
                className='mdd-trf-org-act'
                id={`${org.id}-trf-org-act`}
                onClick={() => onTransferClick(org)}
              >
                <Refresh16 />
                <div>{t('Transfer Ownership')}</div>
              </BaseOrgCard.MenuItem>
            )}
            {canLeave && (
              <BaseOrgCard.MenuItem
                className='mdd-leave-org-act'
                id={`${org.id}-leave-org-act`}
                onClick={() => onLeaveClick(org)}
              >
                <ArrowDown16 />
                <div>{t('Leave Organization')}</div>
              </BaseOrgCard.MenuItem>
            )}
            {canDelete && (
              <BaseOrgCard.MenuItem
                id={`${org.id}-delete-org-act`}
                className='mdd-delete-org-act red'
                onClick={() => onDeleteClick(org)}
              >
                <Trash16 />
                <div>{t('Remove')}</div>
              </BaseOrgCard.MenuItem>
            )}
          </>
        )
      }
    />
  )
}
